import React from 'react';

import { ApiKeyForm } from '@/sections/asset/components/tabs/credentials/credentialForms/ApiKeyForm';
import { BasicLoginForm } from '@/sections/asset/components/tabs/credentials/credentialForms/BasicLoginForm';
import { BearerTokenForm } from '@/sections/asset/components/tabs/credentials/credentialForms/BearerTokenForm';
import { ComplexLoginForm } from '@/sections/asset/components/tabs/credentials/credentialForms/ComplexLoginForm';
import { RecordingForm } from '@/sections/asset/components/tabs/credentials/credentialForms/RecordingForm';
import {
  type ApiKeyFormData,
  type BasicCredentialFormData,
  type BearerTokenFormData,
  type ComplexLoginFormData,
  CredentialType,
  type RecordingCredentialFormData,
} from '@/sections/asset/components/tabs/credentials/credentialForms/types';

interface CredentialFormBodyProps {
  selectedType: CredentialType;
  basicForm: BasicCredentialFormData;
  setBasicForm: React.Dispatch<React.SetStateAction<BasicCredentialFormData>>;
  recordingForm: RecordingCredentialFormData;
  setRecordingForm: React.Dispatch<
    React.SetStateAction<RecordingCredentialFormData>
  >;
  bearerTokenForm: BearerTokenFormData;
  setBearerTokenForm: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
  apiKeyForm: ApiKeyFormData;
  setApiKeyForm: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
  complexLoginForm: ComplexLoginFormData;
  setComplexLoginForm: React.Dispatch<
    React.SetStateAction<ComplexLoginFormData>
  >;
  /**
   * Which slice of the Complex-Login form to render when
   * `selectedType === COMPLEX_LOGIN`. Forwarded to `<ComplexLoginForm section>`;
   * `undefined` falls through to that component's own `'all'` default. Ignored
   * for every other credential type.
   */
  complexLoginSection?: 'all' | 'basics' | 'inputs';
}

/**
 * CredentialFormBody renders the per-type credential form for the
 * asset-agnostic credential types. The Static-Headers and Recipe arms are
 * intentionally NOT handled here — they are host-specific (Static-Headers
 * needs the host's masking decision, Recipe embeds the host's RecipeWizard),
 * so each host (drawer / wizard) renders those itself.
 */
export const CredentialFormBody: React.FC<CredentialFormBodyProps> = ({
  selectedType,
  basicForm,
  setBasicForm,
  recordingForm,
  setRecordingForm,
  bearerTokenForm,
  setBearerTokenForm,
  apiKeyForm,
  setApiKeyForm,
  complexLoginForm,
  setComplexLoginForm,
  complexLoginSection,
}) => {
  switch (selectedType) {
    case CredentialType.BASIC:
      return <BasicLoginForm formData={basicForm} onChange={setBasicForm} />;
    case CredentialType.RECORDING:
      return (
        <RecordingForm formData={recordingForm} onChange={setRecordingForm} />
      );
    case CredentialType.BEARER_TOKEN:
      return (
        <BearerTokenForm
          formData={bearerTokenForm}
          onChange={setBearerTokenForm}
        />
      );
    case CredentialType.API_KEY:
      return <ApiKeyForm formData={apiKeyForm} onChange={setApiKeyForm} />;
    case CredentialType.COMPLEX_LOGIN:
      return (
        <ComplexLoginForm
          formData={complexLoginForm}
          onChange={setComplexLoginForm}
          section={complexLoginSection}
        />
      );
    default:
      // STATIC_TOKEN and RECIPE are host-specific — handled by the caller.
      return null;
  }
};
